import type { IntlShape } from 'react-intl'

import {
  type Icon,
  LockFilledIcon,
  LockIcon,
  OrderedListIcon,
  PlugIcon,
  QuoteIcon,
  SearchAllIcon,
} from '@slite/icons'

export function getAiSearchUseCases(intl: IntlShape) {
  return [
    {
      key: 'sales',
      label: intl.formatMessage({
        id: 'aiSearch.usecase.sales.label',
        defaultMessage: 'Sales',
      }),
      title: intl.formatMessage({
        id: 'aiSearch.usecase.sales.title',
        defaultMessage: 'A 360° view of every account, ready for the call',
      }),
      desc: intl.formatMessage({
        id: 'aiSearch.usecase.sales.desc',
        defaultMessage:
          'Pull notes, deals, and product context from across your stack into one clear answer.',
      }),
      image:
        'https://cdn.sanity.io/media-libraries/mlPfCokLiZ8K/images/containers/391hHthEXynhTIuFeOOaNgnNxp9/super-hero-use-case-sales.png',
    },
    {
      key: 'support',
      label: intl.formatMessage({
        id: 'aiSearch.usecase.support.label',
        defaultMessage: 'Support',
      }),
      title: intl.formatMessage({
        id: 'aiSearch.usecase.support.title',
        defaultMessage: 'Resolve tickets with the right answer, first time',
      }),
      desc: intl.formatMessage({
        id: 'aiSearch.usecase.support.desc',
        defaultMessage:
          'Find prior resolutions, policies, and team notes in seconds — citations included.',
      }),
      image:
        'https://cdn.sanity.io/media-libraries/mlPfCokLiZ8K/images/containers/391hHwNAc1fTArPZ0CODbDr3wPA/super-hero-use-case-support.png',
    },
    {
      key: 'engineering',
      label: intl.formatMessage({
        id: 'aiSearch.usecase.engineering.label',
        defaultMessage: 'Engineering',
      }),
      title: intl.formatMessage({
        id: 'aiSearch.usecase.engineering.title',
        defaultMessage: 'Link tasks, PRs, and deploys for faster rollbacks',
      }),
      desc: intl.formatMessage({
        id: 'aiSearch.usecase.engineering.desc',
        defaultMessage:
          'Investigate incidents with the full thread of context — code, tickets, and chat.',
      }),
      image:
        'https://cdn.sanity.io/media-libraries/mlPfCokLiZ8K/images/containers/391hHqsdQ7s0zUWJsDC6JDwiODC/super-hero-use-case-eng.png',
    },
    {
      key: 'operations',
      label: intl.formatMessage({
        id: 'aiSearch.usecase.operations.label',
        defaultMessage: 'Operations',
      }),
      title: intl.formatMessage({
        id: 'aiSearch.usecase.operations.title',
        defaultMessage: 'Stay audit-ready, ship reports without the hunt',
      }),
      desc: intl.formatMessage({
        id: 'aiSearch.usecase.operations.desc',
        defaultMessage:
          'One question pulls the receipts: policies, owners, decisions, dates.',
      }),
      image:
        'https://cdn.sanity.io/media-libraries/mlPfCokLiZ8K/images/containers/391hHtkxhUkw1NEVynMdH7W1iSC/super-hero-use-case-ops.png',
    },
    {
      key: 'people',
      label: intl.formatMessage({
        id: 'aiSearch.usecase.people.label',
        defaultMessage: 'People',
      }),
      title: intl.formatMessage({
        id: 'aiSearch.usecase.people.title',
        defaultMessage: 'Onboarding answers, where new hires already work',
      }),
      desc: intl.formatMessage({
        id: 'aiSearch.usecase.people.desc',
        defaultMessage:
          'Policies, contacts, and tasks — surfaced the moment a new hire asks.',
      }),
      image:
        'https://cdn.sanity.io/media-libraries/mlPfCokLiZ8K/images/containers/391kiYFHogIFwfYTfTsMTgNGrIk/super-hero-use-case-people.png',
    },
  ]
}

export function getAiSearchWhereYouWork(intl: IntlShape) {
  return [
    {
      title: intl.formatMessage({
        id: 'aiSearch.where.browser.title',
        defaultMessage: 'Anywhere in your browser',
      }),
      desc: intl.formatMessage({
        id: 'aiSearch.where.browser.desc',
        defaultMessage:
          'A Chrome extension that answers from all your sources — and reads the page you’re on.',
      }),
      image:
        'https://cdn.sanity.io/media-libraries/mlPfCokLiZ8K/images/containers/391YkZeXwDA6r8KKFbNf5iJoC4J/super-where-you-work-chrome.png',
    },
    {
      title: intl.formatMessage({
        id: 'aiSearch.where.slack.title',
        defaultMessage: 'Right in Slack',
      }),
      desc: intl.formatMessage({
        id: 'aiSearch.where.slack.desc',
        defaultMessage:
          'Ask in any channel or thread. Get answers with links to the original docs, tickets, and threads.',
      }),
      image:
        'https://cdn.sanity.io/media-libraries/mlPfCokLiZ8K/images/containers/3AAgtMMFZkJjLrbZDWTsPONjtDZ/super-mcp-why-knowsyourteam.png',
    },
    {
      title: intl.formatMessage({
        id: 'aiSearch.where.app.title',
        defaultMessage: 'In the Slite app',
      }),
      desc: intl.formatMessage({
        id: 'aiSearch.where.app.desc',
        defaultMessage:
          'Run searches and workflows, browse cited results, and review scheduled digests — all in one place.',
      }),
      image:
        'https://cdn.sanity.io/media-libraries/mlPfCokLiZ8K/images/containers/3AAgtR32gNc8xCmVcBN8fzOTWUN/super-mcp-why-semantic.png',
    },
  ]
}

type AiSearchHowItem = {
  icon: Icon
  title: string
  desc: string
}

export function getAiSearchHowItWorks(intl: IntlShape): AiSearchHowItem[] {
  return [
    {
      icon: SearchAllIcon,
      title: intl.formatMessage({
        id: 'aiSearch.how.retrieval.title',
        defaultMessage: 'Unified retrieval',
      }),
      desc: intl.formatMessage({
        id: 'aiSearch.how.retrieval.desc',
        defaultMessage:
          'Semantic search across docs, threads, and tickets — plus structured search on tables and dashboards.',
      }),
    },
    {
      icon: OrderedListIcon,
      title: intl.formatMessage({
        id: 'aiSearch.how.orchestration.title',
        defaultMessage: 'Step-by-step orchestration',
      }),
      desc: intl.formatMessage({
        id: 'aiSearch.how.orchestration.desc',
        defaultMessage:
          'Plan, retrieve, rank, and compose — with logs you can review, share, and repeat.',
      }),
    },
    {
      icon: PlugIcon,
      title: intl.formatMessage({
        id: 'aiSearch.how.integrations.title',
        defaultMessage: 'Integration depth',
      }),
      desc: intl.formatMessage({
        id: 'aiSearch.how.integrations.desc',
        defaultMessage:
          'MCP sources plus custom connectors, scoped by space, channel, or folder.',
      }),
    },
    {
      icon: LockIcon,
      title: intl.formatMessage({
        id: 'aiSearch.how.permissions.title',
        defaultMessage: 'Provenance & permissions',
      }),
      desc: intl.formatMessage({
        id: 'aiSearch.how.permissions.desc',
        defaultMessage:
          'Every answer shows where it came from. Permissions are enforced at answer time, not after.',
      }),
    },
    {
      icon: QuoteIcon,
      title: intl.formatMessage({
        id: 'aiSearch.how.accuracy.title',
        defaultMessage: 'Accuracy that holds up',
      }),
      desc: intl.formatMessage({
        id: 'aiSearch.how.accuracy.desc',
        defaultMessage:
          'Results cite their sources and use team context to cut wrong answers and re-work.',
      }),
    },
    {
      icon: LockFilledIcon,
      title: intl.formatMessage({
        id: 'aiSearch.how.safety.title',
        defaultMessage: 'Safety you can trust',
      }),
      desc: intl.formatMessage({
        id: 'aiSearch.how.safety.desc',
        defaultMessage:
          'SOC 2 Type II, HIPAA, and GDPR compliant. Permission-aware outputs keep sensitive data governed.',
      }),
    },
  ]
}

export function getAiSearchMetrics(intl: IntlShape) {
  return [
    {
      value: '90%',
      label: intl.formatMessage({
        id: 'aiSearch.metric.questions',
        defaultMessage: 'fewer internal questions',
      }),
    },
    {
      value: '30s',
      label: intl.formatMessage({
        id: 'aiSearch.metric.guides',
        defaultMessage: 'to draft a customer guide',
      }),
    },
    {
      value: '1,200+',
      label: intl.formatMessage({
        id: 'aiSearch.metric.employees',
        defaultMessage: 'employees on one searchable brain',
      }),
    },
  ]
}
